/**
 * Утилиты для работы с WebAuthn (Passkeys)
 * https://webauthn.guide/
 * 
 * Для нативных приложений (Capacitor) используется плагин capacitor-webauthn
 */

// Импорт Capacitor плагина (Webauthn с маленькой 'a' - так экспортирует плагин)
import { Webauthn as WebAuthnPlugin } from 'capacitor-webauthn';

/**
 * Преобразование base64url в ArrayBuffer
 * @param {string} base64url - Строка в формате base64url
 * @returns {ArrayBuffer}
 */
export function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Преобразование ArrayBuffer в base64url
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Подготовка опций для navigator.credentials.create()
 * @param {Object} options - Опции от сервера
 * @returns {Object}
 */
export function prepareCreateOptions(options) {
  return {
    publicKey: {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      user: {
        ...options.user,
        id: base64urlToBuffer(options.user.id)
      },
      excludeCredentials: options.excludeCredentials?.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      })) || []
    }
  };
}

/**
 * Подготовка опций для navigator.credentials.get()
 * @param {Object} options - Опции от сервера
 * @returns {Object}
 */
export function prepareGetOptions(options) {
  return {
    publicKey: {
      ...options,
      challenge: base64urlToBuffer(options.challenge),
      allowCredentials: options.allowCredentials?.map(cred => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      })) || []
    }
  };
}

/**
 * Сериализация credential для отправки на сервер
 * @param {PublicKeyCredential} credential
 * @returns {Object}
 */
export function serializeCredential(credential) {
  const response = credential.response;
  
  const serialized = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {}
  };
  
  // Для регистрации (attestation)
  if (response.attestationObject) {
    serialized.response.attestationObject = bufferToBase64url(response.attestationObject);
    serialized.response.clientDataJSON = bufferToBase64url(response.clientDataJSON);
    
    // Transports (если доступны)
    if (response.getTransports) {
      serialized.response.transports = response.getTransports();
    }
  }
  
  // Для аутентификации (assertion)
  if (response.authenticatorData) {
    serialized.response.authenticatorData = bufferToBase64url(response.authenticatorData);
    serialized.response.clientDataJSON = bufferToBase64url(response.clientDataJSON);
    serialized.response.signature = bufferToBase64url(response.signature);
    if (response.userHandle) {
      serialized.response.userHandle = bufferToBase64url(response.userHandle);
    }
  }
  
  return serialized;
}

/**
 * Проверка поддержки WebAuthn в браузере
 * @returns {boolean}
 */
export function isWebAuthnSupported() {
  return window.PublicKeyCredential !== undefined;
}

/**
 * Проверка запуска в нативном приложении (Capacitor)
 * @returns {boolean}
 */
export function isNativeApp() {
  // Проверяем разные способы определения Capacitor
  if (typeof window !== 'undefined') {
    // Capacitor 3+
    if (window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()) {
      return true;
    }
    // Capacitor (проверка платформы)
    if (window.Capacitor && window.Capacitor.getPlatform && 
        (window.Capacitor.getPlatform() === 'ios' || window.Capacitor.getPlatform() === 'android')) {
      return true;
    }
    // Cordova fallback
    if (window.cordova) {
      return true;
    }
  }
  return false;
}

/**
 * Проверка поддержки platform authenticator (Touch ID, Face ID, Windows Hello)
 * @returns {Promise<boolean>}
 */
export async function isPlatformAuthenticatorAvailable() {
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isMacOS = /Mac OS X/.test(ua) && !isIOS;
  const isNative = isNativeApp();
  
  // Для нативных приложений (Capacitor) на iOS/Android/macOS 
  // сразу считаем что Passkeys поддерживаются
  // Современные устройства (iOS 16+, Android 9+) поддерживают Passkeys
  if (isNative && (isIOS || isAndroid || isMacOS)) {
    return true;
  }
  
  // Для WKWebView на iOS (когда AppleWebKit есть, но Safari нет)
  if (isIOS && /AppleWebKit/.test(ua) && !/Safari/.test(ua)) {
    return true;
  }
  
  // Для Android WebView
  if (isAndroid && /wv/.test(ua)) {
    return true;
  }
  
  // Стандартная проверка через WebAuthn API
  if (!isWebAuthnSupported()) return false;
  
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Проверка поддержки Conditional UI (автозаполнение)
 * @returns {Promise<boolean>}
 */
export async function isConditionalMediationAvailable() {
  if (!isWebAuthnSupported()) return false;
  
  try {
    if (PublicKeyCredential.isConditionalMediationAvailable) {
      return await PublicKeyCredential.isConditionalMediationAvailable();
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Определение названия устройства из User-Agent
 * @returns {string}
 */
export function detectDeviceName() {
  const ua = navigator.userAgent;
  
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Mac OS X/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Linux/.test(ua)) return 'Linux';
  
  return 'Unknown Device';
}

/**
 * Проверка доступности нативного WebAuthn плагина
 * @returns {Promise<boolean>}
 */
export async function isNativeWebAuthnAvailable() {
  if (!isNativeApp()) {
    return false;
  }
  
  try {
    const result = await WebAuthnPlugin.isWebAuthnAvailable();
    return result.value === true;
  } catch {
    // Если плагин доступен но метод падает - всё равно пробуем использовать нативный
    // На некоторых устройствах isWebAuthnAvailable может не работать
    return true;
  }
}

/**
 * Создание credential (регистрация Passkey)
 * Использует нативный плагин в Capacitor или стандартный API в браузере
 * @param {Object} options - Опции от сервера (raw, без преобразования)
 * @returns {Promise<Object>} - Credential для отправки на сервер
 */
export async function createCredential(options) {
  const isNative = isNativeApp();
  
  // Для нативных приложений используем плагин
  if (isNative) {
    const result = await WebAuthnPlugin.startRegistration(options);
    return result;
  } else {
    // Используем стандартный браузерный API
    const preparedOptions = prepareCreateOptions(options);
    const credential = await navigator.credentials.create(preparedOptions);
    return serializeCredential(credential);
  }
}

/**
 * Получение credential (аутентификация по Passkey)
 * Использует нативный плагин в Capacitor или стандартный API в браузере
 * @param {Object} options - Опции от сервера (raw, без преобразования)
 * @returns {Promise<Object>} - Credential для отправки на сервер
 */
export async function getCredential(options, { mediation, signal } = {}) {
  const isNative = isNativeApp();

  // Для нативных приложений используем плагин
  if (isNative) {
    const result = await WebAuthnPlugin.startAuthentication(options);
    return result;
  } else {
    // Используем стандартный браузерный API
    const preparedOptions = prepareGetOptions(options);
    // FT-079: conditional UI (autofill) — mediation:'conditional' + abort signal (web-only).
    if (mediation) preparedOptions.mediation = mediation;
    if (signal) preparedOptions.signal = signal;
    const credential = await navigator.credentials.get(preparedOptions);
    return serializeCredential(credential);
  }
}

